"use client";

import { useEffect, useState } from "react";
import type { EdgePreview } from "@/lib/pricing/edge-preview";

const EDGE_PREVIEW_LOADING_TIMEOUT_MS = 8_000;
const EDGE_PREVIEW_POLL_MS = 45_000;

export type EdgePreviewState =
  | { status: "loading"; preview: null }
  | { status: "live"; preview: EdgePreview }
  | { status: "fallback"; preview: null };

function hasLiveRows(preview: EdgePreview | null | undefined): preview is EdgePreview {
  return Boolean(preview?.live && preview.rows.length > 0);
}

let current: EdgePreviewState = { status: "loading", preview: null };
const listeners = new Set<() => void>();
let firstLoadSettled = false;
let requestSeq = 0;
let lastPollAt = 0;
let started = false;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let timeoutId: ReturnType<typeof setTimeout> | null = null;

function publish(next: EdgePreviewState) {
  current = next;
  listeners.forEach((notify) => notify());
}

function settleFallback() {
  firstLoadSettled = true;
  if (!hasLiveRows(current.preview)) {
    publish({ status: "fallback", preview: null });
  }
}

async function poll() {
  if (document.hidden) return;
  lastPollAt = Date.now();
  const seq = ++requestSeq;
  const isLatest = () => seq === requestSeq;
  try {
    const res = await fetch("/api/pricing/edge-preview", { cache: "no-store" });
    if (!isLatest()) return;
    if (!res.ok) {
      if (!firstLoadSettled) {
        if (timeoutId) clearTimeout(timeoutId);
        settleFallback();
      }
      return;
    }

    const data = (await res.json()) as EdgePreview;
    if (!isLatest()) return;

    firstLoadSettled = true;
    if (timeoutId) clearTimeout(timeoutId);
    publish(hasLiveRows(data) ? { status: "live", preview: data } : { status: "fallback", preview: null });
  } catch {
    if (!isLatest()) return;
    if (!firstLoadSettled) {
      if (timeoutId) clearTimeout(timeoutId);
      settleFallback();
    }
  }
}

function scheduleNextPoll() {
  const delay = Math.max(0, EDGE_PREVIEW_POLL_MS - (Date.now() - lastPollAt));
  pollTimer = setTimeout(onPollTimer, delay);
}

function onPollTimer() {
  pollTimer = null;
  if (document.hidden) return;
  void poll();
  scheduleNextPoll();
}

function syncPolling() {
  if (pollTimer !== null) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
  if (document.hidden) return;
  if (Date.now() - lastPollAt >= EDGE_PREVIEW_POLL_MS) void poll();
  scheduleNextPoll();
}

function startPolling() {
  if (started) return;
  started = true;
  timeoutId = setTimeout(settleFallback, EDGE_PREVIEW_LOADING_TIMEOUT_MS);
  syncPolling();
  document.addEventListener("visibilitychange", syncPolling);
}

function stopPolling() {
  started = false;
  if (pollTimer !== null) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
  document.removeEventListener("visibilitychange", syncPolling);
  if (timeoutId) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }
}

export function useEdgePreview(): EdgePreviewState {
  const [state, setState] = useState<EdgePreviewState>(current);

  useEffect(() => {
    startPolling();
    const notify = () => setState(current);
    listeners.add(notify);
    notify();
    return () => {
      listeners.delete(notify);
      if (listeners.size === 0) {
        stopPolling();
      }
    };
  }, []);

  return state;
}

export function __resetEdgePreviewForTests() {
  stopPolling();
  current = { status: "loading", preview: null };
  firstLoadSettled = false;
  lastPollAt = 0;
  requestSeq++;
}
